// src/features/simulators/shared/components/EventLog.tsx

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslate } from 'react-admin';
import type { LogEntry } from '../types/simulator.types';

/**
 * Translation keys
 */
const TRANSLATIONS = {
  TITLE: 'resources.simulators.log.title',
  NO_LOGS: 'resources.simulators.log.no_logs',
  SEARCH_PLACEHOLDER: 'resources.simulators.log.search_placeholder',
  FILTER_ALL: 'resources.simulators.log.filter.all',
  FILTER_INFO: 'resources.simulators.log.filter.info',
  FILTER_SUCCESS: 'resources.simulators.log.filter.success',
  FILTER_WARNING: 'resources.simulators.log.filter.warning',
  FILTER_ERROR: 'resources.simulators.log.filter.error',
} as const;

interface EventLogProps {
  logs: LogEntry[];
  maxHeight?: number;
  showFilter?: boolean;
  showSearch?: boolean;
}

type LogFilter = 'all' | 'info' | 'success' | 'warning' | 'error';

/**
 * Get background color based on log type
 */
const getLogBackgroundColor = (type: LogEntry['type']): string => {
  switch (type) {
    case 'info':
      return '#e3f2fd';
    case 'success':
      return '#e8f5e9';
    case 'error':
      return '#ffebee';
    case 'warning':
      return '#fff3e0';
    default:
      return '#f5f5f5';
  }
};

/**
 * Get badge color based on log type
 */
const getBadgeColor = (type: LogEntry['type']): 'info' | 'success' | 'error' | 'warning' | 'default' => {
  switch (type) {
    case 'info':
      return 'info';
    case 'success':
      return 'success';
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    default:
      return 'default';
  }
};

/**
 * Display event logs with search and filter capabilities
 * Shows timestamped log entries with color coding by type
 */
export const EventLog: React.FC<EventLogProps> = ({
  logs,
  maxHeight = 450,
  showFilter = true,
  showSearch = true,
}) => {
  const translate = useTranslate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<LogFilter>('all');

  /**
   * Filter logs based on search term and filter
   */
  const filteredLogs = logs.filter((log) => {
    const matchesSearch = searchTerm === '' ||
      log.message.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' || log.type === filter;

    return matchesSearch && matchesFilter;
  });

  /**
   * Get count for each log type
   */
  const getTypeCount = (type: LogFilter): number => {
    if (type === 'all') return logs.length;
    return logs.filter(log => log.type === type).length;
  };

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {translate(TRANSLATIONS.TITLE)}
          </Typography>
          <Chip
            label={filteredLogs.length}
            color="primary"
            size="small"
          />
        </Box>

        {/* Search Bar */}
        {showSearch && (
          <TextField
            fullWidth
            size="small"
            placeholder={translate(TRANSLATIONS.SEARCH_PLACEHOLDER)}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {/* Filter Buttons */}
        {showFilter && (
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(_, newFilter) => newFilter && setFilter(newFilter)}
              size="small"
              fullWidth
            >
              <ToggleButton value="all">
                {translate(TRANSLATIONS.FILTER_ALL)} ({getTypeCount('all')})
              </ToggleButton>
              <ToggleButton value="info">
                {translate(TRANSLATIONS.FILTER_INFO)} ({getTypeCount('info')})
              </ToggleButton>
              <ToggleButton value="success">
                {translate(TRANSLATIONS.FILTER_SUCCESS)} ({getTypeCount('success')})
              </ToggleButton>
              <ToggleButton value="warning">
                {translate(TRANSLATIONS.FILTER_WARNING)} ({getTypeCount('warning')})
              </ToggleButton>
              <ToggleButton value="error">
                {translate(TRANSLATIONS.FILTER_ERROR)} ({getTypeCount('error')})
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Logs Display */}
        <Paper
          sx={{
            height: maxHeight,
            overflow: 'auto',
            p: 2,
            backgroundColor: '#f5f5f5',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#555',
            },
          }}
        >
          {filteredLogs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
              {logs.length === 0
                ? translate(TRANSLATIONS.NO_LOGS)
                : 'No logs match the current filter'}
            </Typography>
          ) : (
            filteredLogs.map((log) => (
              <Box
                key={log.id}
                sx={{
                  mb: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: getLogBackgroundColor(log.type),
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                {/* Timestamp */}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: '80px', flexShrink: 0 }}
                >
                  {log.timestamp.toLocaleTimeString()}
                </Typography>

                {/* Type Badge */}
                <Chip
                  label={log.type.toUpperCase()}
                  color={getBadgeColor(log.type)}
                  size="small"
                  sx={{ height: 18, fontSize: '0.7rem', minWidth: '60px' }}
                />

                {/* Message */}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ flexGrow: 1, wordBreak: 'break-word' }}
                >
                  {log.message}
                </Typography>

                {/* Session ID (if available) */}
                {log.sessionId && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                  >
                    [{log.sessionId.substring(0, 8)}...]
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Paper>
      </CardContent>
    </Card>
  );
};